// src/pages/employee/EmployeeTasks.jsx
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useMyTasks, updateTaskStatus } from "../../hooks/useFirestore";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

export default function EmployeeTasks() {
  const { userProfile } = useAuth();
  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);
  const { data: tasks, loading } = useMyTasks(userProfile?.uid);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [updating, setUpdating] = useState({});

  const filtered = tasks.filter((t) => {
    const ms = filterStatus === "all" || t.status === filterStatus;
    const mp = filterPriority === "all" || t.priority === filterPriority;
    return ms && mp;
  });

  async function handleStatusChange(taskId, status) {
    setUpdating((prev) => ({ ...prev, [taskId]: true }));
    try {
      await updateTaskStatus(taskId, status);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating((prev) => ({ ...prev, [taskId]: false }));
    }
  }

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <div className="page-header">
        <h1 className="page-title">My Tasks</h1>
      </div>

      <div className="table-filters">
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="PENDING">Todo</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="BLOCKED">Blocked</option>
        </select>
        <select
          className="filter-select"
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          <option value="all">All Priority</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {loading ? (
        <div className="card-empty">No tasks found.</div>
      ) : (
        <div className="task-cards-grid">
          {filtered.map((t) => (
            <div
              key={t.id}
              className={`task-detail-card priority-border-${t.priority}`}
            >
              <div className="task-detail-header">
                <span className="task-detail-title">{t.title}</span>
                <span
                  className={`status-pill status-pill--${t.priority === "high" ? "red" : t.priority === "medium" ? "amber" : "green"}`}
                >
                  {t.priority}
                </span>
              </div>
              {t.description && (
                <p className="task-detail-desc">{t.description}</p>
              )}
              <div className="task-detail-meta">

  <span
    style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="14"
      height="14"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
      />
    </svg>

    Assigned by: {t.assignedByName}
  </span>

  <span
    style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="14"
      height="14"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
      />
    </svg>

    Due:
    {t.deadline?.toDate?.()?.toLocaleDateString?.("en-IN") || "—"}
  </span>

  {t.completedAt && (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="14"
        height="14"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>

      Done:
      {t.completedAt?.toDate?.()?.toLocaleDateString?.("en-IN")}
    </span>
  )}

</div>
              <div className="task-detail-actions">
                <span>Status:</span>
                <select
                  value={t.status}
                  onChange={(e) => handleStatusChange(t.id, e.target.value)}
                  disabled={updating[t.id]}
                  className={`status-select status-select--${
                    t.status === "COMPLETED"
                      ? "green"
                      : t.status === "IN_PROGRESS"
                        ? "blue"
                        : t.status === "BLOCKED"
                          ? "red"
                          : "gray"
                  }`}
                >
                  <option value="PENDING">Todo</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
